// approve_permit_om (OM, 0016): the Operations Manager's independent approval of the
// authorised permit, once per authorisation. No daily cycle can be requested and no work can
// start until it is recorded. Reuses the daily-cycle panel frame and command hook, so the
// signer sends the row version they were shown and acknowledges any change before signing.
import { useId, useState } from 'react'
import { KeyValue, TextArea, Field } from '@/components/ui'
import { OM_APPROVAL_DECLARATION } from '@/domain/omApproval'
import type { ActionPanelProps } from '@/features/contracts'
import { PanelFrame } from '@/features/cycles/frame'
import { usePanelCommand } from '@/features/cycles/hooks'
import { actionArg } from '@/features/cycles/logic'
import { SigningStatement, TickRow, WindowText } from '@/features/cycles/parts'
import { fmtDateTime } from '@/lib/format'

export function OmApprovalPanel({ bundle, action, onDone, onCancel }: ActionPanelProps) {
  const run = usePanelCommand('approve_permit_om', bundle, onDone)
  const [confirmed, setConfirmed] = useState(false)
  const [notes, setNotes] = useState('')
  const [tried, setTried] = useState(false)
  const notesId = useId()

  // The server's hints name what is being approved; fall back to the permit's current records.
  const revisionId = actionArg(action, 'revision_id') ?? bundle.permit.current_revision_id
  const authorisationId = actionArg(action, 'authorisation_id') ?? bundle.permit.current_authorisation_id
  const authorisation = bundle.authorisations.find((a) => a.id === authorisationId) ?? null
  const revision = bundle.revisions.find((r) => r.id === revisionId) ?? null
  const ph = revision?.parties.find((p) => p.role_code === 'PH')?.person ?? null
  const pa = revision?.parties.find((p) => p.role_code === 'PA')?.person ?? null

  const problems = confirmed ? [] : ['Tick the declaration to approve the permit.']

  function submit() {
    setTried(true)
    if (!revisionId || !authorisationId || problems.length > 0) return
    void run.submit({
      permit_id: bundle.permit.id,
      revision_id: revisionId,
      authorisation_id: authorisationId,
      declaration_confirmed: true,
      notes: notes.trim() ? notes.trim() : null,
    })
  }

  return (
    <PanelFrame
      action={action}
      run={run}
      problems={problems}
      tried={tried}
      onCancel={onCancel}
      onSubmit={submit}
      submitLabel="Sign OM approval"
      blockedNote={revisionId && authorisationId ? null : 'The authorisation to approve was not found.'}
    >
      <section className="mb-4 rounded-lg border border-line bg-white p-3" aria-label="What you are approving">
        <p className="mb-2 text-sm font-semibold text-ink">What you are approving</p>
        <KeyValue
          items={[
            ['Permit', `${bundle.permit.permit_number} - ${bundle.permit.title || 'Untitled'}`],
            ['Location', `${bundle.permit.site.name}, ${bundle.permit.site_area.name}`],
            ['Work type', bundle.permit.work_type.description],
            ['Revision', revision ? `Revision ${revision.revision_number}` : 'Not found'],
            [
              'Authorised period',
              authorisation ? <WindowText from={authorisation.authorisation_from} to={authorisation.authorisation_to} /> : 'Not found',
            ],
            [
              'Authorised by',
              authorisation
                ? `${authorisation.attestation.signer.display_name}, ${fmtDateTime(authorisation.attestation.server_signed_at)}`
                : 'Not found',
            ],
            ['Permit Applicant', pa?.display_name ?? 'Not named'],
            ['Permit Holder', ph?.display_name ?? 'Not named'],
          ]}
        />
        <p className="mt-2 text-xs text-ink-soft">
          Read the Application tab before you approve: scope, location, hazards and controls, SIMOPS and the people named. You
          must not be the RS, PA or PH on this permit; the server checks that.
        </p>
      </section>

      <SigningStatement role="OM" person={bundle.viewer.person}>
        <p>{OM_APPROVAL_DECLARATION}</p>
      </SigningStatement>

      <div className="mb-4 rounded-lg border border-line bg-white">
        <TickRow checked={confirmed} onChange={setConfirmed} label="I confirm the declaration above and approve this authorised permit." />
      </div>

      <Field htmlFor={notesId} label="Notes (optional)" hint="Anything you checked or want the RS and PH to know. Saved with your approval.">
        <TextArea id={notesId} rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} />
      </Field>
    </PanelFrame>
  )
}
